<template>
  <div>
    <md-button
      v-if="fab"
      class="md-fab md-fab-top-right"
      @click="showDetailedStats = true"
      style="background-color: white;"
    >
      <div
        style="
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
        "
      >
        <md-progress-spinner
          md-mode="determinate"
          :md-value="progressAverage"
          class="md-primary"
          :md-diameter="56"
        ></md-progress-spinner>
        <div
          style="
            position: absolute;
            font-size: 0.9rem;
            font-weight: bold;
            color: var(--md-theme-default-primary, #0033a0);
          "
        >
          {{ Math.round(progressAverage) }} %
        </div>
      </div>
    </md-button>

    <div v-else>
      <div
        style="
          display: flex;
          align-items: center;
          justify-content: space-between;
        "
      >
        <span
          style="
            font-size: 1.5rem;
            font-weight: bold;
            color: var(--md-theme-default-primary, #0033a0);
          "
          >{{ Math.round(progressAverage) }} %</span
        >
        <md-button
          class="md-icon-button md-primary"
          @click="showDetailedStats = true"
          ><md-icon>info_outline</md-icon></md-button
        >
      </div>
      <md-progress-bar
        md-mode="determinate"
        :md-value="progressAverage"
        class="round-corners"
        style="height: 0.5rem; margin-bottom: 6px;"
      ></md-progress-bar>
      <pre v-if="inDevelopment" style="color: #800;">{{
        {
          progressScore_0,
          progressScore_3,
          progressAverage,
        }
      }}</pre>
    </div>

    <md-dialog :md-active.sync="showDetailedStats">
      <md-dialog-actions>
        <md-button
          @click="showDetailedStats = false"
          class="md-icon-button"
          style="min-width: auto !important;"
          ><md-icon>close</md-icon></md-button
        >
      </md-dialog-actions>
      <md-dialog-content style="max-width: 600px;">
        <div class="md-headline">{{ $t("title") }}</div>
        <div
          id="questionnaireStats"
          style="max-width: 400px; margin-left: auto; margin-right: auto;"
        >
          <div
            class="questionnaireStatsItem"
            v-for="(questionsPerBox, index) in stats.scores"
            :key="questionsPerBox.index"
          >
            <p
              style="
                color: var(--md-theme-default-primary, #0033a0);
                font-weight: bold;
                font-size: 14px;
              "
            >
              {{ questionsPerBox }}
              <span v-if="questionnaire.conditions['score_' + index]">
                /
                {{
                  Math.round(
                    questionnaire.conditions["score_" + index] * sumOfQuestions
                  )
                }}</span
              >
            </p>
            <div class="score-bar-frame round-corners-top md-accent">
              <div
                v-if="questionsPerBox > 0"
                class="score-bar-fill round-corners-top md-primary"
                v-bind:style="{
                  height: (questionsPerBox / sumOfQuestions) * 100 + '%',
                }"
              ></div>
              <div
                v-if="questionnaire.conditions['score_' + index]"
                class="score-bar-range"
                :class="{ 'color-success': stats.conditions['score_' + index] }"
                v-bind:style="{
                  bottom:
                    questionnaire.conditions['score_' + index] * 100 + '%',
                }"
              ></div>
              <md-tooltip v-if="tooltips[index]" md-direction="top">{{
                tooltips[index]
              }}</md-tooltip>
            </div>
            <div>
              {{ parseInt(index, 10) + 1 }}
            </div>
          </div>
        </div>
        <p>{{ $t("progressExplanation") }}</p>
        <div class="md-title">{{ $t("goalTitle") }}</div>
        <p>{{ $t("goalExplanation") }}</p>
        <ol>
          <li>
            {{
              $t("goalCondition1", {
                percentage: questionnaire.conditions.score_0 * 100,
              })
            }}
          </li>
          <li>
            {{
              $t("goalCondition2", {
                percentage: questionnaire.conditions.score_3 * 100,
              })
            }}
          </li>
        </ol>
      </md-dialog-content>
    </md-dialog>
  </div>
</template>

<i18n>
  {
    "en": {
      "title": "Your Progress in Detail",
      "progressExplanation": "When you answer a question correctly, it moves to the next level. Once you have answered a question correctly three times, it is in your long-term memory and will no longer appear.",
      "goalTitle": "Goal",
      "goalExplanation": "To successfully complete the questionnaire, two criteria must be met:",
      "goalCondition1": "Less than {percentage} % of the questions in level 1",
      "goalCondition2": "More than {percentage} % of the questions in level 4"
    },
    "de": {
      "title": "Dein Fortschritt im Detail",
      "progressExplanation": "Wenn du eine Frage richtig beantwortest, wandert sie in die nächste Stufe. Sobald du eine Frage dreimal richtig beantwortet hast, ist sie in deinem Langzeitgedächtnis und erscheint nicht mehr.",
      "goalTitle": "Ziel",
      "goalExplanation": "Um den Fragebogen erfolgreich abzuschließen, müssen zwei Kriterien erfüllt sein:",
      "goalCondition1": "Weniger als {percentage} % der Fragen in Stufe 1",
      "goalCondition2": "Mehr als {percentage} % der Fragen in Stufe 4"
    }
  }
</i18n>

<script>
export default {
  props: {
    questionnaire: {
      type: Object,
      required: true,
    },
    fab: {
      type: Boolean,
    },
  },
  data: function () {
    return {
      tooltips: [
        "Max. " + this.questionnaire.conditions.score_0 * 100 + " % der Fragen",
        null,
        null,
        "Min. " + this.questionnaire.conditions.score_3 * 100 + " % der Fragen",
      ],
      showDetailedStats: false,
    };
  },
  computed: {
    stats() {
      return (
        this.$store.getters[`${this.questionnaire.identifier}/stats`] || {}
      );
    },
    sumOfQuestions() {
      return (
        this.stats.scores[0] +
        this.stats.scores[1] +
        this.stats.scores[2] +
        this.stats.scores[3]
      );
    },
    progressScore_0() {
      if (this.stats.scores[0] === 0) {
        return 1; // Return 100% progress when there are no remaining questions
      }
      return (
        1 -
        (this.stats.scores[0] -
          Math.round(
            this.questionnaire.conditions.score_0 * this.sumOfQuestions
          )) /
          this.stats.scores[0]
      );
    },
    progressScore_3() {
      return (
        this.stats.scores[3] /
        Math.round(this.questionnaire.conditions.score_3 * this.sumOfQuestions)
      );
    },
    progressAverage() {
      return ((this.progressScore_0 + this.progressScore_3) / 2) * 100;
    },
    inDevelopment() {
      return process.env.NODE_ENV !== "production";
    },
  },
};
</script>
<style lang="scss" scoped>
.round-corners-top {
  border-top-left-radius: 0.5em;
  border-top-right-radius: 0.5em;
}
#questionnaireStats {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 2rem;
  padding: 0 2rem;
}
.questionnaireStatsItem {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.score-bar-frame {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 2rem;
  height: 6rem;
  background: rgba(#0033a0, 0.1);
  margin-bottom: 0.5rem;
  overflow: hidden;
}
.score-bar-fill {
  width: 1rem;
  height: 0;
  background: var(--md-theme-default-primary, #0033a0);
}
.score-bar-range {
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: -10;
  width: 2.5rem;
  height: 2px;
  background: #a0a0a0;
}
.color-success {
  background: rgba(#40e058, 0.5);
}
</style>
